import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Globe, Code, TrendingUp, Bot, CheckCircle, Users, Award, Zap } from "lucide-react";
import Navbar from "../components/Navbar";
import PageMeta from "../components/PageMeta";
import Footer from "../components/Footer";
import FloatingFAQ from "../components/FloatingFAQ";

// Hero Slides Data
const heroSlides = [
  {
    tag: "Digital Agency",
    title: "We design",
    highlight: "digital",
    subtitle: "experiences",
    description: "Building websites, software solutions, and marketing strategies that help businesses stand out and grow.",
    image: "/assets/hero/hero-1.jpg",
    cta: { text: "Start a Project", link: "/consultation" }
  },
  {
    tag: "Custom Solutions",
    title: "Your vision,",
    highlight: "our",
    subtitle: "expertise",
    description: "Every business is unique. We craft custom digital solutions that reflect your brand identity and drive real results.",
    image: "/assets/hero/hero-2.jpg",
    cta: { text: "See Our Work", link: "/portfolio" }
  },
  {
    tag: "Our Portfolio",
    title: "Real results",
    highlight: "for real",
    subtitle: "businesses",
    description: "From fintech platforms to local businesses, we've helped companies across industries achieve their digital goals.",
    image: "/assets/hero/hero-3.jpg",
    cta: { text: "View Portfolio", link: "/portfolio" }
  }
];

// Hero Carousel - No dots
const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      if (!isAnimating) {
        setIsAnimating(true);
        setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
        setTimeout(() => setIsAnimating(false), 500);
      }
    }, 15000);
    return () => clearInterval(timer);
  }, [isAnimating]);

  const slide = heroSlides[currentSlide];

  return (
    <section className="min-h-screen flex items-center relative overflow-hidden pt-20" data-testid="hero-section">
      {heroSlides.map((s, index) => (
        <div 
          key={index}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === currentSlide ? 'opacity-30' : 'opacity-0'
          }`}
          style={{
            backgroundImage: `url(${s.image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
      ))}
      
      <div className="absolute inset-0 bg-gradient-to-b from-[#0F0F11] via-transparent to-[#0F0F11]" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0F0F11] via-transparent to-transparent" />
      <div className="hero-glow absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
      
      <div className="container-main relative z-10">
        <div className="max-w-5xl">
          <div className="mb-8">
            <span className="tag">{slide.tag}</span>
          </div>
          
          <h1 
            className={`text-5xl sm:text-6xl md:text-8xl lg:text-9xl font-bold mb-8 leading-[0.9] transition-all duration-500 ${
              isAnimating ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
            }`}
            style={{ fontFamily: '"Clash Display", sans-serif' }}
          >
            {slide.title}<br />
            <span style={{ color: '#D4FF00' }}>{slide.highlight}</span><br />
            {slide.subtitle}
          </h1>
          
          <p 
            className={`text-base md:text-lg max-w-md mb-12 transition-all duration-500 delay-100 ${
              isAnimating ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
            }`}
            style={{ color: '#A1A1AA' }}
          >
            {slide.description}
          </p>
          
          <div className="flex flex-wrap gap-4">
            <Link to={slide.cta.link} className="btn-primary" data-testid="hero-cta-primary">
              {slide.cta.text} <ArrowRight size={16} />
            </Link>
            <Link to="/contact" className="btn-outline" data-testid="hero-cta-secondary">
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

// $500 Intro Offer Banner
const OfferBanner = () => {
  return (
    <section className="py-10" data-testid="offer-banner">
      <div className="container-main">
        <div className="pricing-card featured relative">
          <span className="pricing-badge">Limited Intro Offer</span>
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
            <div>
              <h2
                className="text-2xl md:text-4xl font-bold mb-2 normal-case"
                style={{ fontFamily: '"Clash Display", sans-serif' }}
              >
                Your first website for <span style={{ color: '#D4FF00' }}>$500</span>
              </h2>
              <p className="text-sm md:text-base" style={{ color: '#A1A1AA' }}>
                A professional 4 page website with custom design, contact form, and basic SEO. Hosting from $20 per month.
              </p>
            </div>
            <Link to="/launch" className="btn-primary whitespace-nowrap" data-testid="offer-banner-cta">
              Claim Offer <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

// Animated Counter Component
const AnimatedCounter = ({ target, suffix = '', prefix = '', duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          const startTime = Date.now();
          const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            // Ease out cubic
            const easeOut = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(easeOut * target));
            if (progress < 1) {
              requestAnimationFrame(animate);
            }
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [target, duration, hasAnimated]);

  return <span ref={ref}>{prefix}{count}{suffix}</span>;
};

// Why Choose Us Section with Animated Counters
const WhyChooseUsSection = () => {
  const features = [
    {
      icon: Zap,
      title: "Fast Delivery",
      stat: { value: 2, suffix: "-4 Weeks", isText: true, display: "2-4 Weeks" },
      description: "From concept to launch, we deliver quality work on tight timelines without compromising standards."
    },
    {
      icon: Users,
      title: "Dedicated Support",
      stat: { value: 24, suffix: "/7", isText: true, display: "24/7" },
      description: "Your success is our priority. Get direct access to our team whenever you need assistance."
    },
    {
      icon: Award,
      title: "Proven Results",
      stat: { value: 50, suffix: "+", isText: false },
      description: "Projects delivered successfully across various industries with measurable business impact."
    },
    {
      icon: CheckCircle,
      title: "Client Satisfaction",
      stat: { value: 98, suffix: "%", isText: false },
      description: "Our clients love working with us. We build lasting partnerships, not just websites."
    }
  ];

  return (
    <section className="section-padding" style={{ background: '#0F0F11' }} data-testid="why-choose-us">
      <div className="container-main">
        <div className="text-center mb-16">
          <span className="tag mb-4">Why Averoan</span>
          <h2 
            className="text-4xl md:text-6xl font-bold mt-4 mb-6"
            style={{ fontFamily: '"Clash Display", sans-serif' }}
          >
            Why clients <span style={{ color: '#D4FF00' }}>choose</span> us
          </h2>
          <p className="text-base md:text-lg max-w-2xl mx-auto" style={{ color: '#A1A1AA' }}>
            We combine technical expertise with creative vision to deliver digital solutions that drive real business growth.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <div 
              key={feature.title}
              className="p-8 rounded-2xl text-center group hover:border-[#D4FF00] transition-all duration-300"
              style={{ background: '#18181B', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <div 
                className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 transition-transform group-hover:scale-110"
                style={{ background: 'rgba(212, 255, 0, 0.1)' }}
              >
                <feature.icon size={28} style={{ color: '#D4FF00' }} />
              </div>
              <div 
                className="text-4xl md:text-5xl font-bold mb-2"
                style={{ color: '#D4FF00', fontFamily: '"Clash Display", sans-serif' }}
              >
                {feature.stat.isText ? (
                  feature.stat.display
                ) : (
                  <AnimatedCounter target={feature.stat.value} suffix={feature.stat.suffix} />
                )}
              </div>
              <h3 
                className="text-xl font-bold mb-3 normal-case"
                style={{ fontFamily: '"Clash Display", sans-serif' }}
              >
                {feature.title}
              </h3>
              <p className="text-sm" style={{ color: '#A1A1AA' }}>
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Trust Indicators */}
        <div className="mt-16 p-8 rounded-2xl" style={{ background: '#18181B', border: '1px solid rgba(255,255,255,0.05)' }}>
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-center md:text-left">
              <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: '"Clash Display", sans-serif' }}>
                Ready to grow your business?
              </h3>
              <p style={{ color: '#A1A1AA' }}>Let's discuss your project and create something amazing together.</p>
            </div>
            <Link 
              to="/consultation" 
              className="btn-primary whitespace-nowrap"
            >
              Start a Project <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section - Editorial Alternating Layout
const ServicesSection = () => {
  const services = [
    {
      num: "01",
      icon: Globe,
      title: "Web Development",
      subtitle: "Websites that convert",
      description: "Custom websites that captivate your audience and convert visitors into loyal customers. From landing pages to full-scale e-commerce.",
      link: "/services/web-development",
      highlights: ["Custom Design", "E-Commerce", "CMS", "Responsive"],
      image: "/assets/services/web-development.png",
      accent: "#D4FF00"
    },
    {
      num: "02",
      icon: Code,
      title: "Custom Software",
      subtitle: "Built for your business",
      description: "Tailored solutions that automate workflows, manage data, and scale with your growth. No off-the-shelf compromises.",
      link: "/services/custom-software",
      highlights: ["CRM Systems", "Dashboards", "APIs", "Cloud"],
      image: "/assets/services/custom-software.png",
      accent: "#00D4FF"
    },
    {
      num: "03",
      icon: Bot,
      title: "AI Automation",
      subtitle: "Intelligence at scale",
      description: "Leverage artificial intelligence to streamline operations, enhance customer experiences, and unlock new business possibilities.",
      link: "/services/ai-automation",
      highlights: ["Chatbots", "Automation", "ML Models", "Integration"],
      image: "/assets/services/ai-automation.png",
      accent: "#D946EF"
    },
    {
      num: "04",
      icon: TrendingUp,
      title: "Growth Marketing",
      subtitle: "Results that matter",
      description: "Data-driven SEO, paid advertising, and content strategies that put your business in front of the right audience and deliver real ROI.",
      link: "/services/growth-marketing",
      highlights: ["SEO", "PPC", "Content", "Analytics"],
      image: "/assets/services/growth-marketing.png",
      accent: "#F97316"
    }
  ];

  return (
    <section className="section-padding" data-testid="services-section">
      <div className="container-main">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-20">
          <div>
            <span className="tag mb-4">What We Do</span>
            <h2
              className="text-4xl md:text-6xl font-bold mt-4"
              style={{ fontFamily: '"Clash Display", sans-serif' }}
            >
              Services
            </h2>
          </div>
          <Link
            to="/services"
            className="flex items-center gap-2 text-sm font-medium hover:text-[#D4FF00] transition-colors"
            style={{ color: '#A1A1AA' }}
            data-testid="services-view-all"
          >
            All Services <ArrowUpRight size={14} />
          </Link>
        </div>

        <div className="flex flex-col gap-24 md:gap-32">
          {services.map((service, index) => {
            const isReversed = index % 2 !== 0;
            return (
              <Link
                key={service.title}
                to={service.link}
                className="group block"
                data-testid={`service-card-${service.num}`}
              >
                <div className={`flex flex-col ${isReversed ? 'md:flex-row-reverse' : 'md:flex-row'} gap-8 md:gap-14 items-center`}>
                  {/* Image Side */}
                  <div className="w-full md:w-[55%] relative">
                    <div className="relative rounded-2xl overflow-hidden aspect-[16/10]">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors duration-500" />
                      <div
                        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                        style={{ background: `linear-gradient(135deg, ${service.accent}10 0%, transparent 60%)` }}
                      />
                    </div>
                    {/* Floating number */}
                    <div
                      className={`absolute -bottom-8 ${isReversed ? '-left-4 md:-left-8' : '-right-4 md:-right-8'} text-[120px] md:text-[180px] font-bold leading-none select-none`}
                      style={{
                        fontFamily: '"Clash Display", sans-serif',
                        color: 'transparent',
                        WebkitTextStroke: `2px ${service.accent}30`,
                      }}
                    >
                      {service.num}
                    </div>
                  </div>

                  {/* Content Side */}
                  <div className="w-full md:w-[45%] relative z-10">
                    <div
                      className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110"
                      style={{ background: `${service.accent}10`, border: `1px solid ${service.accent}25` }}
                    >
                      <service.icon size={26} style={{ color: service.accent }} />
                    </div>

                    <p
                      className="text-xs font-semibold uppercase tracking-[0.2em] mb-3"
                      style={{ color: service.accent }}
                    >
                      {service.subtitle}
                    </p>

                    <h3
                      className="text-3xl md:text-4xl lg:text-5xl font-bold mb-5 normal-case transition-colors duration-300 group-hover:text-[#D4FF00]"
                      style={{ fontFamily: '"Clash Display", sans-serif', lineHeight: 1.1 }}
                    >
                      {service.title}
                    </h3>

                    <p
                      className="text-base md:text-lg leading-relaxed mb-8"
                      style={{ color: '#A1A1AA' }}
                    >
                      {service.description}
                    </p>

                    <div className="flex flex-wrap gap-2 mb-8">
                      {service.highlights.map((h) => (
                        <span
                          key={h}
                          className="px-4 py-1.5 text-xs font-medium rounded-full"
                          style={{ background: 'rgba(255,255,255,0.05)', color: '#A1A1AA', border: '1px solid rgba(255,255,255,0.08)' }}
                        >
                          {h}
                        </span>
                      ))}
                    </div>

                    <div className="inline-flex items-center gap-3 group/arrow">
                      <span
                        className="text-sm font-semibold uppercase tracking-wider transition-colors duration-300"
                        style={{ color: service.accent }}
                      >
                        Explore
                      </span>
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 group-hover:translate-x-1"
                        style={{ background: `${service.accent}15`, border: `1px solid ${service.accent}30` }}
                      >
                        <ArrowRight size={16} style={{ color: service.accent }} />
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Featured Work with Scrolling Images on Hover
const FeaturedWork = () => {
  const projects = [
    {
      title: "Fonderx Exchange",
      category: "Fintech Platform",
      description: "A modern cryptocurrency exchange platform",
      url: "https://fonderx.com",
      image: "/assets/portfolio/fonderx.png"
    },
    {
      title: "Maple Kids Academy",
      category: "Childcare Website",
      description: "Professional website for childcare services",
      url: "https://mkids.ca",
      image: "/assets/portfolio/maple-kids.png"
    },
    {
      title: "Vanguard PM",
      category: "Professional Contractor",
      description: "Professional contracting and maintenance services",
      url: "https://vanguardpm.ca",
      image: "/assets/portfolio/vanguard-pm.png"
    },
    {
      title: "VanSign",
      category: "Signage Company",
      description: "Vancouver's premium signage company website",
      url: "https://vansign.ca",
      image: "/assets/portfolio/vansign.png"
    }
  ];

  return (
    <section className="section-padding" style={{ background: '#18181B' }} data-testid="featured-work">
      <div className="container-main">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <span className="tag mb-4">Portfolio</span>
            <h2 
              className="text-4xl md:text-6xl font-bold mt-4"
              style={{ fontFamily: '"Clash Display", sans-serif' }}
            >
              Our Work
            </h2>
          </div>
          <Link 
            to="/portfolio" 
            className="flex items-center gap-2 text-sm font-medium hover:text-[#D4FF00] transition-colors"
            style={{ color: '#A1A1AA' }}
            data-testid="portfolio-view-all"
          >
            View All <ArrowUpRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {projects.map((project, index) => (
            <a
              key={project.title}
              href={project.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group block rounded-2xl overflow-hidden"
              style={{ background: '#0F0F11', border: '1px solid rgba(255,255,255,0.1)' }}
              data-testid={`featured-project-${index}`}
            >
              <div className="relative overflow-hidden">
                <img 
                  src={project.image} 
                  alt={project.title}
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              </div>
              <div className="p-6">
                <span className="text-xs uppercase tracking-wider" style={{ color: '#D4FF00' }}>{project.category}</span>
                <h3 
                  className="text-xl md:text-2xl font-bold mt-2 mb-2 normal-case group-hover:text-[#D4FF00] transition-colors"
                  style={{ fontFamily: '"Clash Display", sans-serif' }}
                >
                  {project.title}
                </h3>
                <p className="text-sm" style={{ color: '#A1A1AA' }}>{project.description}</p>
                <div className="mt-4 flex items-center gap-2 text-sm font-medium group-hover:text-[#D4FF00] transition-colors">
                  Visit Website <ArrowUpRight size={14} />
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <section className="section-padding relative overflow-hidden" data-testid="cta-section">
      <div className="hero-glow absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-50" />
      
      <div className="container-main relative z-10">
        <div className="text-center max-w-3xl mx-auto">
          <h2 
            className="text-4xl md:text-6xl lg:text-7xl font-bold mb-8"
            style={{ fontFamily: '"Clash Display", sans-serif' }}
          >
            Ready to<br />
            <span style={{ color: '#D4FF00' }}>stand out?</span>
          </h2>
          <p className="text-base md:text-lg mb-10 max-w-lg mx-auto" style={{ color: '#A1A1AA' }}>
            Let's discuss how we can help your business grow with a stunning digital presence.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/consultation" className="btn-primary" data-testid="cta-contact">
              Get Started <ArrowRight size={16} />
            </Link>
            <Link to="/pricing" className="btn-outline" data-testid="cta-pricing">
              View Pricing
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <div className="noise-bg">
      <PageMeta
        title="Averoan Technologies | Web Design & Digital Agency in Vancouver BC"
        description="Custom websites, software, AI automation and growth marketing for small businesses. Your first website from $500. Vancouver, BC."
      />
      <Navbar />
      <HeroSection />
      <OfferBanner />
      <WhyChooseUsSection />
      <ServicesSection />
      <FeaturedWork />
      <CTASection />
      <Footer />
      <FloatingFAQ />
    </div>
  );
}
